// Maya Premium checkout & purchase-lookup server functions.
// - createMayaCheckoutSession: opens an embedded Stripe checkout for either
//   a single-destination unlock ($2.99) or the Maya Pass México ($9.99).
// - getMayaPurchaseBySession: reads a purchase by Stripe session id for the
//   post-payment return page (email is masked).
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import {
  type StripeEnv,
  createStripeClient,
  getStripeErrorMessage,
} from "@/lib/stripe.server";


export const MAYA_PRODUCTS = {
  destination: "maya_destination_one_time",
  tour: "maya_tour_one_time",
  pass: "maya_pass_one_time",
} as const;

type CheckoutResult =
  | { clientSecret: string }
  | { error: string };

export const createMayaCheckoutSession = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        product: z.enum([
          MAYA_PRODUCTS.destination,
          MAYA_PRODUCTS.tour,
          MAYA_PRODUCTS.pass,
        ]),
        destinationKey: z.string().trim().min(1).max(160).optional(),
        tourSlug: z.string().trim().min(1).max(160).optional(),
        customerEmail: z.string().trim().email().max(255).optional(),
        userId: z.string().trim().max(64).optional(),
        returnUrl: z.string().trim().url(),
        environment: z.enum(["sandbox", "live"]),
        lang: z.enum(["es", "en", "fr", "pt", "it", "de"]).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }): Promise<CheckoutResult> => {
    try {
      // SECURITY: return_url must resolve to our own origin (open-redirect defense).
      try {
        const req = getRequest();
        const hostHeader = req?.headers?.get("host") ?? "";
        const parsedReturn = new URL(data.returnUrl);
        const allowedHosts = new Set(
          [
            hostHeader,
            "mostrandomexico.mx",
            "www.mostrandomexico.mx",
            "mostrandomexico.lovable.app",
          ].filter(Boolean),
        );
        const hostOk =
          allowedHosts.has(parsedReturn.host) ||
          /\.lovable\.app$/i.test(parsedReturn.host);
        if (!hostOk) return { error: "ERR_GENERIC" };
      } catch {
        return { error: "ERR_GENERIC" };
      }

      // SECURITY: Never trust a client-supplied userId. If a Supabase bearer
      // token accompanies the call, verify and use the verified `sub`.
      let verifiedUserId: string | null = null;
      try {
        const req = getRequest();
        const authHeader = req?.headers?.get("authorization") ?? "";
        if (authHeader.startsWith("Bearer ")) {
          const token = authHeader.slice("Bearer ".length).trim();
          if (token) {
            const { createClient } = await import("@supabase/supabase-js");
            const sb = createClient(
              process.env.SUPABASE_URL!,
              process.env.SUPABASE_PUBLISHABLE_KEY!,
              { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
            );
            const { data: claimsData, error } = await sb.auth.getClaims(token);
            if (!error && claimsData?.claims?.sub) {
              verifiedUserId = String(claimsData.claims.sub);
            }
          }
        }
      } catch (e) {
        console.warn("[maya/checkout] token verification skipped", e);
      }

      // Anti pruebas de tarjetas: límites por IP/correo/sesión y bloqueo tras rechazos.
      const { fraudKeys, checkMayaCheckoutAllowed } = await import("@/lib/maya-fraud.server");
      const req = getRequest();
      const { getCookie } = await import("@tanstack/react-start/server");
      const fk = fraudKeys({
        ip: req?.headers?.get("cf-connecting-ip") ?? req?.headers?.get("x-forwarded-for")?.split(",")[0] ?? null,
        email: data.customerEmail ?? null,
        session: getCookie("mm_rk") ?? null,
      });
      const block = await checkMayaCheckoutAllowed(fk);
      if (block) return { error: block === "too_many_declines" ? "ERR_DECLINES" : "ERR_ATTEMPTS" };

      const stripe = createStripeClient(data.environment as StripeEnv);
      const prices = await stripe.prices.list({ lookup_keys: [data.product] });
      if (!prices.data.length) return { error: "ERR_PRICE" };
      const price = prices.data[0];

      const productId =
        typeof price.product === "string" ? price.product : price.product.id;
      const product = await stripe.products.retrieve(productId);

      // Shared metadata for BOTH the Checkout Session and the PaymentIntent.
      // Attaching it to the PI (via payment_intent_data.metadata) keeps
      // `payment_intent.succeeded`, `charge.refunded`, and `charge.dispute.*`
      // events identifiable as Maya even though they arrive without the
      // Session in the payload.
      const sharedMetadata: Record<string, string> = {
        kind: "maya",
        product: data.product,
        ...(data.destinationKey && { destination_key: data.destinationKey }),
        ...(data.tourSlug && { tour_slug: data.tourSlug }),
        ...(verifiedUserId && { user_id: verifiedUserId }),
        ...(data.lang && { lang: data.lang }),
        ...(fk.email && { fk_email: fk.email }),
        ...(fk.ip && { fk_ip: fk.ip }),
      };

      const session = await stripe.checkout.sessions.create({
        line_items: [{ price: price.id, quantity: 1 }],
        mode: "payment",
        ui_mode: "embedded_page",
        return_url: data.returnUrl,
        ...(data.customerEmail && { customer_email: data.customerEmail }),
        payment_intent_data: {
          description: product.name,
          metadata: sharedMetadata,
        },
        metadata: sharedMetadata,
      });

      return { clientSecret: session.client_secret ?? "" };
    } catch (err) {
      console.error("[maya/checkout] failed", getStripeErrorMessage(err));
      return { error: "ERR_GENERIC" };
    }
  });


// Fetch a single Maya purchase by Stripe Checkout session id. Used by the
// `/maya/return` page to confirm what was purchased. The customer email is
// returned MASKED (e.g. `j***@example.com`) because the Stripe session_id
// lands in the browser URL bar and can leak via Referer headers, access
// logs, browser history, or shared links.
export interface MayaPurchaseSummary {
  found: boolean;
  product: string | null;
  destinationKey: string | null;
  customerEmailMasked: string | null;
  amount: number | null;
  currency: string | null;
  status: string | null;
  createdAt: string | null;
}

function maskEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  const at = email.indexOf("@");
  if (at <= 0) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const head = local.slice(0, 1);
  return `${head}${"*".repeat(Math.max(1, local.length - 1))}@${domain}`;
}

export const getMayaPurchaseBySession = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ sessionId: z.string().trim().min(8).max(256) }).parse(d),
  )
  .handler(async ({ data }): Promise<MayaPurchaseSummary> => {
    const empty: MayaPurchaseSummary = {
      found: false,
      product: null,
      destinationKey: null,
      customerEmailMasked: null,
      amount: null,
      currency: null,
      status: null,
      createdAt: null,
    };
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: row } = await supabaseAdmin
      .from("maya_purchases")
      .select("product_id, destination_key, customer_email, amount, currency, status, created_at")
      .eq("stripe_session_id", data.sessionId)
      .maybeSingle();
    if (!row) return empty;
    return {
      found: true,
      product: row.product_id,
      destinationKey: row.destination_key,
      customerEmailMasked: maskEmail(row.customer_email),
      amount: row.amount,
      currency: row.currency,
      status: row.status,
      createdAt: row.created_at,
    };
  });

export interface MayaOfferPrice {
  amountMinor: number;
  currency: string;
  /** Equivalente aproximado en MXN (tipo de cambio público del día), o null. */
  approxMxn: number | null;
}

export interface MayaOffer {
  prices: Partial<Record<(typeof MAYA_PRODUCTS)[keyof typeof MAYA_PRODUCTS], MayaOfferPrice>>;
  /** Guías de destino publicadas hoy (Pass solo si son 5 o más). */
  guidesCount: number;
}

let fxCache: { at: number; rates: Record<string, number> } | null = null;

async function usdRates(): Promise<Record<string, number> | null> {
  if (fxCache && Date.now() - fxCache.at < 6 * 3600_000) return fxCache.rates;
  try {
    const r = await fetch("https://open.er-api.com/v6/latest/USD", { signal: AbortSignal.timeout(3000) });
    const j = (await r.json()) as { rates?: Record<string, number> };
    if (!j.rates?.MXN) return null;
    fxCache = { at: Date.now(), rates: j.rates };
    return j.rates;
  } catch {
    return null;
  }
}

/** Precio real de Stripe por lookup_key para cada producto Maya. */
export const getMayaOffer = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ environment: z.enum(["sandbox", "live"]) }).parse(d))
  .handler(async ({ data }): Promise<MayaOffer> => {
    const out: MayaOffer = { prices: {}, guidesCount: 0 };
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { count } = await (supabaseAdmin as any)
        .from("maya_destinations")
        .select("id", { count: "exact", head: true })
        .eq("status", "published");
      out.guidesCount = count ?? 0;
    } catch {
      /* sin conteo: el Pass queda oculto */
    }
    try {
      const stripe = createStripeClient(data.environment as StripeEnv);
      const keys = Object.values(MAYA_PRODUCTS);
      const [prices, rates] = await Promise.all([
        stripe.prices.list({ lookup_keys: keys, active: true }),
        usdRates(),
      ]);
      for (const p of prices.data) {
        const key = p.lookup_key as (typeof keys)[number] | null;
        if (!key || p.unit_amount == null) continue;
        const cur = p.currency.toUpperCase();
        let approxMxn: number | null = null;
        if (cur === "MXN") approxMxn = null;
        else if (rates?.MXN && (cur === "USD" || rates[cur])) {
          const usd = cur === "USD" ? p.unit_amount / 100 : p.unit_amount / 100 / rates[cur];
          approxMxn = Math.round(usd * rates.MXN);
        }
        out.prices[key] = { amountMinor: p.unit_amount, currency: cur, approxMxn };
      }
    } catch (err) {
      console.error("[maya/offer]", getStripeErrorMessage(err));
    }
    return out;
  });
